import * as i1$1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { inject, Injectable, Pipe, input, computed, Component, Input, ChangeDetectionStrategy, NgModule } from '@angular/core';
import * as i1 from '@skyux/theme';
import { SkyThemeIconManifestService, SkyThemeComponentClassDirective, SkyThemeService } from '@skyux/theme';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';
import { switchMap, catchError, of } from 'rxjs';

/**
 * @internal
 */
class SkyIconResolverService {
    #glyphMap = new Map();
    #manifestSvc = inject(SkyThemeIconManifestService);
    constructor() {
        // Map the icons by name for more efficient lookup.
        for (const glyph of this.#manifestSvc.getManifest().glyphs) {
            // TODO: keep this until `faName` is removed just in case any icons get added with the wrong shape
            if (glyph.faName && !glyph.faNames) {
                glyph.faNames = [glyph.faName];
            }
            this.#glyphMap.set(glyph.name, glyph);
        }
    }
    resolveIcon(icon, variant, themeSettings) {
        let iconType = 'fa';
        const variantIcon = variant && `${icon}-${variant}`;
        const lineIcon = `${icon}-line`;
        // Get the specified variant, or fall back to the icon name.
        // If the pure icon name doesn't exist, try the line variant.
        let glyph = this.#glyphMap.get(variantIcon) ??
            this.#glyphMap.get(icon) ??
            this.#glyphMap.get(lineIcon) ??
            Array.from(this.#glyphMap.values()).find((glyph) => glyph.aliases?.includes(variantIcon) ||
                glyph.aliases?.includes(lineIcon) ||
                glyph.aliases?.includes(icon));
        // If still no icon is found, search through the icons that match the FA name.
        if (!glyph) {
            let glyphs = Array.from(this.#glyphMap.values()).filter((g) => g.faNames?.includes(icon));
            if (glyphs.length) {
                if (glyphs.length > 1) {
                    // If multiples are found, make sure we select the correct variant, if requested.
                    // The icons are ordered such that the "default" variant will be the first one.
                    if (variant) {
                        const variantGlyph = glyphs.find((g) => g.name.endsWith(variant));
                        if (variantGlyph) {
                            glyphs = [variantGlyph];
                        }
                    }
                }
                glyph = glyphs[0];
            }
            // If none of the above works, no matching icon was found
        }
        if (glyph) {
            // If a glyph is found, use it for modern theme or if no FA fallback exists.
            if (themeSettings?.theme.name === 'modern' || !glyph.faNames?.length) {
                icon = glyph.name;
                iconType = 'skyux';
            }
            else {
                // For default theme, use a known FA fallback.
                // If the icon name requested is already a FA icon, just use it. Otherwise use the first one.
                if (!glyph.faNames?.includes(icon)) {
                    icon = glyph.faNames[0];
                }
            }
        }
        // If no SKY UX glyph is found, assume icon is part of FA and pass it along.
        return {
            icon,
            iconType,
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyIconResolverService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyIconResolverService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyIconResolverService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [] });

/**
 * @internal
 */
class SkyIconClassListPipe {
    #resolver = inject(SkyIconResolverService);
    transform(icon, themeSettings, size, fixedWidth, variant) {
        let classList;
        const { icon: resolvedIcon, iconType: resolvedIconType } = this.#resolver.resolveIcon(icon, variant, themeSettings);
        if (resolvedIconType === 'skyux') {
            classList = ['sky-i-' + resolvedIcon];
        }
        else {
            classList = ['fa', 'fa-' + resolvedIcon];
        }
        if (size) {
            classList.push('fa-' + size);
        }
        if (fixedWidth) {
            classList.push('fa-fw');
        }
        return classList;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyIconClassListPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.2.7", ngImport: i0, type: SkyIconClassListPipe, isStandalone: false, name: "skyIconClassList" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyIconClassListPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'skyIconClassList',
                    standalone: false,
                }]
        }] });

async function getIconMap() {
    const response = await fetch(`https://sky.blackbaudcdn.net/static/skyux-icons/7/assets/svg/skyux-icons.svg`);
    /* istanbul ignore next */
    if (!response.ok) {
        throw new Error('Icon sprite could not be loaded.');
    }
    const markup = await response.text();
    document.body.insertAdjacentHTML('afterbegin', markup);
    const iconMap = Array.from(document.querySelectorAll('#sky-icon-svg-sprite symbol')).reduce((map, el) => {
        const idParts = el.id.split('-');
        // Construct the icon name by removing `sky-i-` from the beginning
        // and `-<size>-<variant>` from the end.
        const name = idParts.slice(2, idParts.length - 2).join('-');
        let sizes = map.get(name);
        if (!sizes) {
            sizes = [];
            map.set(name, sizes);
        }
        // The penultimate segment is the size for which the icon has
        // been optimized.
        sizes.push(+idParts[idParts.length - 2]);
        return map;
    }, new Map());
    // Sort all the sizes for later comparison.
    for (const id of iconMap.keys()) {
        // Dedupe and sort the icon sizes.
        iconMap.set(id, [...new Set(iconMap.get(id))].sort());
    }
    return iconMap;
}
function getNearestSize(iconMap, name, pixelSize) {
    const sizes = iconMap.get(name);
    if (sizes) {
        let nearestSizeUnder = -Infinity;
        let nearestSizeOver = Infinity;
        for (const availableSize of sizes) {
            if (availableSize === pixelSize) {
                return pixelSize;
            }
            else if (availableSize < pixelSize) {
                nearestSizeUnder = availableSize;
            }
            else {
                nearestSizeOver = availableSize;
                break;
            }
        }
        const underDiff = Math.abs(pixelSize - nearestSizeUnder);
        const overDiff = Math.abs(pixelSize - nearestSizeOver);
        return isNaN(overDiff) || underDiff < overDiff
            ? nearestSizeUnder
            : nearestSizeOver;
    }
    return undefined;
}
let iconMapPromise;
/**
 * @internal
 */
class SkyIconSvgResolverService {
    async resolveHref(name, pixelSize = 16, variant = 'line') {
        if (!iconMapPromise) {
            iconMapPromise = getIconMap();
        }
        const iconMap = await iconMapPromise;
        let href = `#sky-i-${name}`;
        // Find the icon with the optimal size nearest to the requested size.
        const nearestSize = getNearestSize(iconMap, name, pixelSize);
        if (!nearestSize) {
            throw new Error(`Icon with name '${name}' was not found.`);
        }
        href = `${href}-${nearestSize}-${variant}`;
        return href;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyIconSvgResolverService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyIconSvgResolverService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyIconSvgResolverService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

const SIZE_BASE = 16;
const RELATIVE_SIZES = new Map([
    ['md', SIZE_BASE],
    ['lg', 21.333 /* SIZE_BASE * (4/3) */],
    ['2x', 32 /* SIZE_BASE * 2 */],
    ['3x', 48 /* SIZE_BASE * 3 */],
    ['4x', 64 /* SIZE_BASE * 4 */],
    ['5x', 80 /* SIZE_BASE * 5 */],
]);
const FIXED_SIZES = new Map([
    ['s', SIZE_BASE],
    ['m', 20],
    ['l', 24],
    ['xl', 32],
]);
function defaultSize(value) {
    return value ?? 'm';
}
/**
 * @internal
 */
class SkyIconSvgComponent {
    constructor() {
        this.#resolverSvc = inject(SkyIconSvgResolverService);
        this.iconName = input.required();
        this.iconSize = input('m', {
            transform: defaultSize,
        });
        this.relativeSize = input();
        this.iconVariant = input();
        this.#iconInfo = computed(() => {
            return {
                src: this.iconName(),
                relativeSize: this.relativeSize(),
                iconSize: this.iconSize(),
                variant: this.iconVariant(),
            };
        });
        this.iconHref = toSignal(toObservable(this.#iconInfo).pipe(switchMap((info) => this.#resolverSvc.resolveHref(info.src, info.relativeSize !== undefined
            ? RELATIVE_SIZES.get(info.relativeSize)
            : FIXED_SIZES.get(info.iconSize), info.variant)), catchError(() => of(''))));
    }
    #resolverSvc;
    #iconInfo;
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyIconSvgComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "19.2.7", type: SkyIconSvgComponent, isStandalone: true, selector: "sky-icon-svg", inputs: { iconName: { classPropertyName: "iconName", publicName: "iconName", isSignal: true, isRequired: true, transformFunction: null }, iconSize: { classPropertyName: "iconSize", publicName: "iconSize", isSignal: true, isRequired: false, transformFunction: null }, relativeSize: { classPropertyName: "relativeSize", publicName: "relativeSize", isSignal: true, isRequired: false, transformFunction: null }, iconVariant: { classPropertyName: "iconVariant", publicName: "iconVariant", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "relativeSize() ? \"sky-icon-svg-relative-\" + relativeSize() : \"sky-icon-svg-\" + iconSize()" } }, hostDirectives: [{ directive: i1.SkyThemeComponentClassDirective }], ngImport: i0, template: "<div class=\"sky-icon-svg-container\">\n  <svg\n    aria-hidden=\"true\"\n    class=\"sky-icon-svg-img\"\n    [attr.data-sky-icon]=\"iconName()\"\n    [attr.data-sky-icon-variant]=\"iconVariant()\"\n  >\n    <use [attr.xlink:href]=\"iconHref()\" />\n  </svg>\n</div>\n", styles: ["@charset \"UTF-8\";:host.sky-cmp-theme-default{--sky-override-sky-icon-svg-size-xxxs: 4px;--sky-override-sky-icon-svg-size-xxs: 8px;--sky-override-sky-icon-svg-size-xs: 12px;--sky-override-sky-icon-svg-size-s: 16px;--sky-override-sky-icon-svg-size-m: 20px;--sky-override-sky-icon-svg-size-l: 24px;--sky-override-sky-icon-svg-size-xl: 32px;--sky-override-sky-icon-svg-size-xxl: 40px;--sky-override-sky-icon-svg-size-xxxl: 48px}:host{display:inline-flex;--sky-icon-svg-img-size: var( --sky-override-sky-icon-svg-size-s, var(--sky-size-icon-s) )}:host.sky-icon-svg-relative-md{--sky-icon-svg-img-size: 1em}:host.sky-icon-svg-relative-lg .sky-icon-svg-img{transform:scale(1.33)}:host.sky-icon-svg-relative-lg .sky-icon-svg-container{margin:0 .166em}:host.sky-icon-svg-relative-2x{--sky-icon-svg-img-size: 2em}:host.sky-icon-svg-relative-3x{--sky-icon-svg-img-size: 3em}:host.sky-icon-svg-relative-4x{--sky-icon-svg-img-size: 4em}:host.sky-icon-svg-relative-5x{--sky-icon-svg-img-size: 5em}:host.sky-icon-svg-xxxs{--sky-icon-svg-img-size: var( --sky-override-sky-icon-svg-size-xxxs, var(--sky-size-icon-xxxs) )}:host.sky-icon-svg-xxs{--sky-icon-svg-img-size: var( --sky-override-sky-icon-svg-size-xxs, var(--sky-size-icon-xxs) )}:host.sky-icon-svg-xs{--sky-icon-svg-img-size: var( --sky-override-sky-icon-svg-size-xs, var(--sky-size-icon-xs) )}:host.sky-icon-svg-m{--sky-icon-svg-img-size: var( --sky-override-sky-icon-svg-size-m, var(--sky-size-icon-m) )}:host.sky-icon-svg-l{--sky-icon-svg-img-size: var( --sky-override-sky-icon-svg-size-l, var(--sky-size-icon-l) )}:host.sky-icon-svg-xl{--sky-icon-svg-img-size: var( --sky-override-sky-icon-svg-size-xl, var(--sky-size-icon-xl) )}:host.sky-icon-svg-xxl{--sky-icon-svg-img-size: var( --sky-override-sky-icon-svg-size-xxl, var(--sky-size-icon-xxl) )}:host.sky-icon-svg-xxxl{--sky-icon-svg-img-size: var( --sky-override-sky-icon-svg-size-xxxl, var(--sky-size-icon-xxxl) )}.sky-icon-svg-container{height:var(--sky-icon-svg-img-size);width:var(--sky-icon-svg-img-size);display:inline-flex;align-items:center}.sky-icon-svg-container:before{content:\"\\200b\"}.sky-icon-svg-container svg{height:100%;width:100%}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyIconSvgComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sky-icon-svg', host: {
                        '[class]': 'relativeSize() ? "sky-icon-svg-relative-" + relativeSize() : "sky-icon-svg-" + iconSize()',
                    }, hostDirectives: [SkyThemeComponentClassDirective], template: "<div class=\"sky-icon-svg-container\">\n  <svg\n    aria-hidden=\"true\"\n    class=\"sky-icon-svg-img\"\n    [attr.data-sky-icon]=\"iconName()\"\n    [attr.data-sky-icon-variant]=\"iconVariant()\"\n  >\n    <use [attr.xlink:href]=\"iconHref()\" />\n  </svg>\n</div>\n", styles: ["@charset \"UTF-8\";:host.sky-cmp-theme-default{--sky-override-sky-icon-svg-size-xxxs: 4px;--sky-override-sky-icon-svg-size-xxs: 8px;--sky-override-sky-icon-svg-size-xs: 12px;--sky-override-sky-icon-svg-size-s: 16px;--sky-override-sky-icon-svg-size-m: 20px;--sky-override-sky-icon-svg-size-l: 24px;--sky-override-sky-icon-svg-size-xl: 32px;--sky-override-sky-icon-svg-size-xxl: 40px;--sky-override-sky-icon-svg-size-xxxl: 48px}:host{display:inline-flex;--sky-icon-svg-img-size: var( --sky-override-sky-icon-svg-size-s, var(--sky-size-icon-s) )}:host.sky-icon-svg-relative-md{--sky-icon-svg-img-size: 1em}:host.sky-icon-svg-relative-lg .sky-icon-svg-img{transform:scale(1.33)}:host.sky-icon-svg-relative-lg .sky-icon-svg-container{margin:0 .166em}:host.sky-icon-svg-relative-2x{--sky-icon-svg-img-size: 2em}:host.sky-icon-svg-relative-3x{--sky-icon-svg-img-size: 3em}:host.sky-icon-svg-relative-4x{--sky-icon-svg-img-size: 4em}:host.sky-icon-svg-relative-5x{--sky-icon-svg-img-size: 5em}:host.sky-icon-svg-xxxs{--sky-icon-svg-img-size: var( --sky-override-sky-icon-svg-size-xxxs, var(--sky-size-icon-xxxs) )}:host.sky-icon-svg-xxs{--sky-icon-svg-img-size: var( --sky-override-sky-icon-svg-size-xxs, var(--sky-size-icon-xxs) )}:host.sky-icon-svg-xs{--sky-icon-svg-img-size: var( --sky-override-sky-icon-svg-size-xs, var(--sky-size-icon-xs) )}:host.sky-icon-svg-m{--sky-icon-svg-img-size: var( --sky-override-sky-icon-svg-size-m, var(--sky-size-icon-m) )}:host.sky-icon-svg-l{--sky-icon-svg-img-size: var( --sky-override-sky-icon-svg-size-l, var(--sky-size-icon-l) )}:host.sky-icon-svg-xl{--sky-icon-svg-img-size: var( --sky-override-sky-icon-svg-size-xl, var(--sky-size-icon-xl) )}:host.sky-icon-svg-xxl{--sky-icon-svg-img-size: var( --sky-override-sky-icon-svg-size-xxl, var(--sky-size-icon-xxl) )}:host.sky-icon-svg-xxxl{--sky-icon-svg-img-size: var( --sky-override-sky-icon-svg-size-xxxl, var(--sky-size-icon-xxxl) )}.sky-icon-svg-container{height:var(--sky-icon-svg-img-size);width:var(--sky-icon-svg-img-size);display:inline-flex;align-items:center}.sky-icon-svg-container:before{content:\"\\200b\"}.sky-icon-svg-container svg{height:100%;width:100%}\n"] }]
        }] });

class SkyIconComponent {
    constructor() {
        /**
         * The icon size. Size is independent of font size.
         * @default "m"
         */
        this.iconSize = input();
        this.themeSvc = inject(SkyThemeService, { optional: true });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyIconComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.7", type: SkyIconComponent, isStandalone: false, selector: "sky-icon", inputs: { icon: { classPropertyName: "icon", publicName: "icon", isSignal: false, isRequired: false, transformFunction: null }, iconName: { classPropertyName: "iconName", publicName: "iconName", isSignal: false, isRequired: false, transformFunction: null }, iconType: { classPropertyName: "iconType", publicName: "iconType", isSignal: false, isRequired: false, transformFunction: null }, size: { classPropertyName: "size", publicName: "size", isSignal: false, isRequired: false, transformFunction: null }, fixedWidth: { classPropertyName: "fixedWidth", publicName: "fixedWidth", isSignal: false, isRequired: false, transformFunction: null }, variant: { classPropertyName: "variant", publicName: "variant", isSignal: false, isRequired: false, transformFunction: null }, iconSize: { classPropertyName: "iconSize", publicName: "iconSize", isSignal: true, isRequired: false, transformFunction: null } }, ngImport: i0, template: "@if (iconName) {\n  <sky-icon-svg\n    [iconName]=\"iconName\"\n    [relativeSize]=\"size\"\n    [iconSize]=\"iconSize()\"\n    [iconVariant]=\"variant\"\n    [attr.data-sky-icon]=\"iconName\"\n    [attr.data-sky-icon-variant]=\"variant\"\n  />\n} @else if (icon) {\n  <i\n    aria-hidden=\"true\"\n    class=\"sky-icon\"\n    [attr.data-sky-icon]=\"icon\"\n    [attr.data-sky-icon-type]=\"iconType\"\n    [attr.data-sky-icon-variant]=\"variant\"\n    [ngClass]=\"\n      icon\n        | skyIconClassList\n          : (themeSvc?.settingsChange | async)?.currentSettings\n          : size\n          : fixedWidth\n          : variant\n    \"\n  ></i>\n}\n", styles: [":host{display:inline-flex}i{display:inherit}\n"], dependencies: [{ kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "component", type: SkyIconSvgComponent, selector: "sky-icon-svg", inputs: ["iconName", "iconSize", "relativeSize", "iconVariant"] }, { kind: "pipe", type: i1$1.AsyncPipe, name: "async" }, { kind: "pipe", type: SkyIconClassListPipe, name: "skyIconClassList" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyIconComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sky-icon', changeDetection: ChangeDetectionStrategy.OnPush, standalone: false, template: "@if (iconName) {\n  <sky-icon-svg\n    [iconName]=\"iconName\"\n    [relativeSize]=\"size\"\n    [iconSize]=\"iconSize()\"\n    [iconVariant]=\"variant\"\n    [attr.data-sky-icon]=\"iconName\"\n    [attr.data-sky-icon-variant]=\"variant\"\n  />\n} @else if (icon) {\n  <i\n    aria-hidden=\"true\"\n    class=\"sky-icon\"\n    [attr.data-sky-icon]=\"icon\"\n    [attr.data-sky-icon-type]=\"iconType\"\n    [attr.data-sky-icon-variant]=\"variant\"\n    [ngClass]=\"\n      icon\n        | skyIconClassList\n          : (themeSvc?.settingsChange | async)?.currentSettings\n          : size\n          : fixedWidth\n          : variant\n    \"\n  ></i>\n}\n", styles: [":host{display:inline-flex}i{display:inherit}\n"] }]
        }], propDecorators: { icon: [{
                type: Input
            }], iconName: [{
                type: Input
            }], iconType: [{
                type: Input
            }], size: [{
                type: Input
            }], fixedWidth: [{
                type: Input
            }], variant: [{
                type: Input
            }] } });

class SkyIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.7", ngImport: i0, type: SkyIconModule, declarations: [SkyIconClassListPipe, SkyIconComponent], imports: [CommonModule, SkyIconSvgComponent], exports: [SkyIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyIconModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SkyIconClassListPipe, SkyIconComponent],
                    imports: [CommonModule, SkyIconSvgComponent],
                    exports: [SkyIconComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SkyIconModule, SkyIconSvgResolverService, SkyIconComponent as λ1 };
